<template>
  <div class="flex gap-4 relative">
    <OverlaySpinner :show="moreCasesLoading" :contained="true" :sticky="true" />

    <template v-if="!(xs && isSidebarVisible)">
      <VCard
        v-if="vitalSignsDisplayMode.display === DisplayMode.COMPACT"
        class="grid bg-white rounded-md pt-1 px-4 overflow-x-auto gap-0 flex-1 h-fit"
        :style="`grid-template-columns: minmax(140px, 2fr) minmax(${isSidebarVisible ? '100px' : '130px'}, 1.2fr) repeat(4, fit-content(100%)) ${isEdsEnabled ? 'fit-content(100%) ' : ''} minmax(${isSidebarVisible ? '160px' : '170px'}, 1.5fr) auto`"
      >
        <span
          class="table-header justify-self-start px-2 py-1 mb-7 cursor-pointer hover:bg-[#ededef] flex items-start w-full"
          :class="{ sorted: vitalSignsSortBy.sortBy === AllCaseOrderOptionsDTO.PATIENT_NAME }"
          @click="$emit('sort-requested', AllCaseOrderOptionsDTO.PATIENT_NAME)"
          data-testid="patient-name-header"
        >
          {{ t('Last name / First name').toLocaleUpperCase() }}
          <VIcon
            :icon="
              vitalSignsSortBy.sortBy === AllCaseOrderOptionsDTO.PATIENT_NAME
                ? sortIcon
                : 'arrow_upward'
            "
            size="18"
            class="ml-1 sort-icon"
          />
        </span>

        <span
          class="table-header justify-self-center px-2 py-1 mb-7 flex items-start w-full"
          :class="{ sorted: vitalSignsSortBy.sortBy === AllCaseOrderOptionsDTO.INV_S_BP }"
          data-testid="blood-pressure-header"
        >
          {{ getLabelWithDefaultUnit(Parameter.BloodPressure).toLocaleUpperCase() }}
        </span>

        <span
          :class="[
            commonHeaderClasses,
            { sorted: vitalSignsSortBy.sortBy === AllCaseOrderOptionsDTO.HEART_RATE }
          ]"
          @click="$emit('sort-requested', AllCaseOrderOptionsDTO.HEART_RATE)"
          data-testid="heart-rate-header"
        >
          {{ getLabelWithDefaultUnit(Parameter.HeartRate, true).toLocaleUpperCase() }}
          <VIcon
            :icon="
              vitalSignsSortBy.sortBy === AllCaseOrderOptionsDTO.HEART_RATE
                ? sortIcon
                : 'arrow_upward'
            "
            size="18"
            class="ml-1 sort-icon"
          />
        </span>

        <span
          :class="[
            commonHeaderClasses,
            { sorted: vitalSignsSortBy.sortBy === AllCaseOrderOptionsDTO.SPO2 }
          ]"
          @click="$emit('sort-requested', AllCaseOrderOptionsDTO.SPO2)"
          data-testid="oxygen-saturation-header"
        >
          {{ getLabelWithDefaultUnit(Parameter.OxygenSaturationSPO2, true).toLocaleUpperCase() }}
          <VIcon
            :icon="
              vitalSignsSortBy.sortBy === AllCaseOrderOptionsDTO.SPO2 ? sortIcon : 'arrow_upward'
            "
            size="18"
            class="ml-1 sort-icon"
          />
        </span>

        <span
          :class="[
            commonHeaderClasses,
            { sorted: vitalSignsSortBy.sortBy === AllCaseOrderOptionsDTO.RR }
          ]"
          @click="$emit('sort-requested', AllCaseOrderOptionsDTO.RR)"
          data-testid="respiratory-rate-header"
        >
          {{ getLabelWithDefaultUnit(Parameter.RespiratoryRate, true).toLocaleUpperCase() }}
          <VIcon
            :icon="
              vitalSignsSortBy.sortBy === AllCaseOrderOptionsDTO.RR ? sortIcon : 'arrow_upward'
            "
            size="18"
            class="ml-1 sort-icon"
          />
        </span>

        <span
          :class="[
            commonHeaderClasses,
            { sorted: vitalSignsSortBy.sortBy === AllCaseOrderOptionsDTO.TEMP }
          ]"
          @click="$emit('sort-requested', AllCaseOrderOptionsDTO.TEMP)"
          data-testid="temperature-header"
        >
          {{ getLabelWithDefaultUnit(Parameter.Temperature, true).toLocaleUpperCase() }}
          <VIcon
            :icon="
              vitalSignsSortBy.sortBy === AllCaseOrderOptionsDTO.TEMP ? sortIcon : 'arrow_upward'
            "
            size="18"
            class="ml-1 sort-icon"
          />
        </span>

        <span
          v-if="isEdsEnabled"
          class="table-header w-full flex flex-col items-start justify-self-center content-start text-nowrap gap-1 py-1 mb-7"
          data-testid="eds-score-header-wrapper"
        >
          <div
            class="px-2 cursor-pointer hover:bg-[#ededef] flex w-full"
            :class="{ sorted: vitalSignsSortBy.sortBy === AllCaseOrderOptionsDTO.EDS_SCORE }"
            data-testid="eds-score-header"
            @click="$emit('sort-requested', AllCaseOrderOptionsDTO.EDS_SCORE)"
          >
            {{ t('EDS Score').toLocaleUpperCase() }}
            <VIcon
              :icon="
                vitalSignsSortBy.sortBy === AllCaseOrderOptionsDTO.EDS_SCORE
                  ? sortIcon
                  : 'arrow_upward'
              "
              size="18"
              class="ml-1 sort-icon"
            />
          </div>

          <VTooltip location="bottom left" :open-delay="500" :disabled="!hasAcceptedEdsLimitation">
            <template #activator="{ props }">
              <button
                v-bind="props"
                @click="$emit('open-eds-sidebar')"
                class="ml-2"
                :class="[
                  {
                    'text-primary-600 font-semibold text-no-wrap p-0': !hasAcceptedEdsLimitation
                  }
                ]"
              >
                <VIcon
                  v-if="hasAcceptedEdsLimitation"
                  icon="info"
                  color="primary-600"
                  class="material-icons-outlined"
                  size="18"
                />
                <template v-else>
                  <VIcon icon="lock" class="material-icons-outlined" size="18" />
                  {{ t('Show') }}
                </template>
              </button>
            </template>
            <span>{{ t('Display Information') }}</span>
          </VTooltip>
        </span>

        <span
          :class="[
            commonHeaderClasses,
            { sorted: vitalSignsSortBy.sortBy === AllCaseOrderOptionsDTO.ORGAN_SUPPORT }
          ]"
          data-testid="organ-support-header"
          @click="$emit('sort-requested', AllCaseOrderOptionsDTO.ORGAN_SUPPORT)"
        >
          {{ t('Organ Support').toLocaleUpperCase() }}
          <VIcon
            :icon="
              vitalSignsSortBy.sortBy === AllCaseOrderOptionsDTO.ORGAN_SUPPORT
                ? sortIcon
                : 'arrow_upward'
            "
            size="18"
            class="ml-1 sort-icon"
          />
        </span>

        <span />

        <CompactPatientCard
          v-for="patientCase in filteredCases"
          :key="patientCase.case.id"
          :caze="patientCase.case"
          :vital-signs="patientCase.vitalSigns"
          :organ-support="patientCase.organSupport"
          :is-sidebar-visible="isSidebarVisible"
          :eds-score="patientCase.edsScore"
          @edit-patient="$emit('edit-patient', $event)"
          @close-case="(caseId, discharge) => $emit('close-case', caseId, discharge)"
          @emergency-click="openEmergencyModal"
          @open-eds-sidebar="$emit('open-eds-sidebar')"
        />
      </VCard>

      <div v-else class="flex flex-col gap-6 flex-1">
        <ExpandedPatientCard
          v-for="patientCase in filteredCases"
          :key="patientCase.case.id"
          :caze="patientCase.case"
          :vital-signs="patientCase.vitalSigns"
          :organ-support="patientCase.organSupport"
          :is-sidebar-visible="isSidebarVisible"
          :eds-score="patientCase.edsScore"
          @emergency-click="openEmergencyModal"
          @open-eds-sidebar="$emit('open-eds-sidebar')"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { inject, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useDisplay } from 'vuetify/framework'
import CompactPatientCard from '../molecules/CompactPatientCard.vue'
import ExpandedPatientCard from '../molecules/ExpandedPatientCard.vue'
import OverlaySpinner from '@/components/common/OverlaySpinner.vue'
import { DisplayMode } from '@/stores/models/VitalSignsDisplayMode'
import { usePluginsStore } from '@/stores/plugins'
import type { OpenEmergencyModalFn } from '@/types/OpenEmergencyModalFn'
import { AllCaseOrderOptionsDTO, CasesWithVitalSignsFilterDTOSortDirectionEnum } from '@/generated'
import useParameterLabel from '@/composables/useParameterLabel'
import { Parameter } from '@/enums/Parameter'
import { useEdsPlugin } from '@/composables/useEdsPlugin'

const { t } = useI18n()
const { xs } = useDisplay()
const { isEdsEnabled } = storeToRefs(usePluginsStore())
const { getLabelWithDefaultUnit } = useParameterLabel()
const { hasAcceptedEdsLimitation } = useEdsPlugin()

const openEmergencyModal = inject<OpenEmergencyModalFn>('openEmergencyModal', () => {})

const props = defineProps<{
  filteredCases: any[]
  vitalSignsDisplayMode: { display: DisplayMode }
  vitalSignsSortBy: {
    sortBy: AllCaseOrderOptionsDTO
    sortDirection: CasesWithVitalSignsFilterDTOSortDirectionEnum
  }
  isSidebarVisible: boolean
  moreCasesLoading: boolean
}>()

defineEmits(['open-eds-sidebar', 'sort-requested', 'edit-patient', 'close-case'])

const commonHeaderClasses =
  'table-header justify-self-center px-2 py-1 mb-7 cursor-pointer hover:bg-[#ededef] flex items-start w-full'

const sortIcon = computed(() => {
  return props.vitalSignsSortBy.sortDirection ===
    CasesWithVitalSignsFilterDTOSortDirectionEnum.ASCENDING
    ? 'arrow_upward'
    : 'arrow_downward'
})
</script>

<style scoped>
.table-header {
  font-size: 11px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.5px;
  text-align: left;
  color: #1e21348a;
}

.table-header.sorted {
  min-width: 80px;
  font-weight: 700;
  color: #414651;
}

.sort-icon {
  visibility: hidden;
}

.table-header:hover .sort-icon {
  visibility: visible;
}

.table-header.sorted .sort-icon {
  visibility: visible;
}
</style>
